<template>
  <sc-render :fields="fields" :empty-message="$t('NO_DATA_SOURCE_EXIST')">
    <div
      class="container"
      :class="[themeClass, imagePositionClass]"
    >
      <div class="content-wrapper">
        <component
          :is="componentType"
          :field="!isEditMode && ctaField"
          class="image-wrapper"
        >
          <sc-image v-if="isEditMode" :media="imageField" />
          <img v-else-if="showImage" :src="imageSrc" :alt="imageAlt" />
        </component>
        <article class="text-block" :style="mobileDescriptionStyle">
          <component
            :is="componentType"
            :field="!isEditMode && ctaField"
          >
            <heading
              v-if="showTitle"
              class="title"
              desktopSize="size-3"
              mobileSize="size-5"
              :tagField="headingSizeField"
              :textField="titleField"
            />
            <sc-text
              v-if="showDescription"
              class="description"
              tag="p"
              :field="descriptionField"
            />
            <sc-text
              v-if="showTag"
              class="tag"
              tag="p"
              :field="tagField"
            />
          </component>
          <sh-button
            v-if="showCta"
            class="cta"
            :href="ctaHref"
            :color="cta_color"
            :outline="isOutline"
            :naked="displayCtaAsLink"
            :aria-label="ctaHiddenText || ctaTitle"
          >
            {{ ctaText }}
          </sh-button>
        </article>
      </div>
    </div>
  </sc-render>
</template>
<script>
  import { Image, Link, Text } from '@sitecore-jss/sitecore-jss-vue'
  import { createNamespacedHelpers } from 'vuex'
  import mapFields from '~/helpers/map-fields'
  import ScRender from '../sc-render/index'
  import Heading from '../heading'

  const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
  const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

  const DISPLAY_STYLE = {
    LINK: 'link',
    BUTTON: 'button',
    TAG: 'tag'
  }

  export default {
    name: 'EditorialLandscapeImage',
    props: {
      fields: {
        type: Object,
        default: () => ({})
      }
    },
    components: {
      ScImage: Image,
      ScLink: Link,
      ScText: Text,
      ScRender,
      Heading
    },
    computed: {
      ...mapJssGetters(['isEditMode', 'colorTheme']),
      ...mapEnvGetters(['isDarkTheme']),
      ...mapFields({
        imageField: 'image',
        imageSrc: 'image.value.src',
        imageAlt: 'image.value.alt',
        imagePosition: 'imagePosition.value',
        titleField: 'title',
        headingSizeField: 'headingSize',
        descriptionField: 'description',
        tagField: 'tag',
        tagText: 'tag.value',
        ctaField: 'cta.value',
        ctaText: 'cta.value.text',
        ctaHref: 'cta.value.href',
        ctaTitle: 'cta.value.title',
        ctaHiddenText: 'cta.value.data-hidden-text',
        style: 'style.value',
        buttonColor: 'buttonColor.value',
        hideDescriptionForMobile: 'hideDescriptionForMobile.value',
        ctaColor: 'buttonColor.value'
      }),
      cta_color () {
        return this.ctaColor ? this.ctaColor.split(' ')[0] : ''
      },
      cta_isTransparent () {
        return !!(this.ctaColor && this.ctaColor.split(' ')[1] === 'outlined')
      },
      isOutline () {
        return this.cta_isTransparent && !this.displayCtaAsLink
      },
      themeClass () {
        return `theme-${this.colorTheme}`.toLowerCase()
      },
      componentType () {
        if (!this.isEditMode && this.ctaHref) {
          return 'sc-link'
        }
        return 'div'
      },
      imagePositionClass () {
        return `image-${this.imagePosition || 'left'}`.toLowerCase()
      },
      showImage () {
        return !this.isEditMode || !!this.imageSrc
      },
      showTitle () {
        return this.isEditMode || this.titleField
      },
      showDescription () {
        return this.isEditMode || this.descriptionField
      },
      showTag () {
        return this.isEditMode || !!this.tagText
      },
      showCta () {
        return this.style !== DISPLAY_STYLE.TAG && this.ctaHref
      },
      displayCtaAsLink () {
        return this.style === DISPLAY_STYLE.LINK
      },
      mobileDescriptionStyle () {
        const displayValue = this.hideDescriptionForMobile ? 'none' : 'block'
        return {
          // the CSS variable is set in accordance with the hidden description setting
          '--display-description': displayValue
        }
      }
    }
  }
</script>
<style lang="scss" scoped>
  .container {
    @include stack-space($space-090);

    @include tablet {
      @include stack-space(0);

      .image-wrapper {
        padding-left: $space-060;
        padding-right: $space-080;
      }

      .text-block {
        @include reset;
        padding-right: $space-060;
      }

      &.image-right {
        .content-wrapper {
          flex-direction: row-reverse;
        }

        .text-block {
          @include reset;
          padding-left: $space-060;
        }

        .image-wrapper {
          padding-right: $space-060;
          padding-left: $space-080;
        }
      }
    }
  }

  .content-wrapper {
    display: flex;
    flex-direction: column;
    text-decoration: none;

    @include tablet {
      flex-direction: row;
      padding: $space-100 0;
    }
  }

  .text-block {
    @include inset-space($space-050);
    padding-bottom: 0;
    text-decoration: none;

    @include tablet {
      flex: 0 0 70%;
      @include reset;
      padding-right: $space-080;
    }

    a {
      text-decoration: none;
    }
  }

  .title {
    padding: 0;
    @include stack-space($space-030);

    @include tablet {
      @include stack-space($space-050);
    }
  }

  .description {
    display: var(--display-description);
    @include font('040');
    @include stack-space($space-030);

    @include tablet {
      display: block;
      @include font('070');
      @include stack-space($space-050);
    }
  }

  .tag {
    @include reset;
    @include font('030');
    text-transform: uppercase;

    @include tablet {
      @include font('040');
    }
  }

  .cta {
    margin-top: $space-050;

    @include tablet {
      margin-top: $space-060;
    }
  }

  .image-wrapper {
    display: block;

    @include tablet {
      flex: 0 0 30%;
    }

    ::v-deep img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .theme-black {
    .title,
    .description,
    .tag {
      color: $color-white;
    }

    a:focus {
      outline-color: $color-white;
    }
  }
</style>
